import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Typography, Space } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  CloudOutlined,
  SafetyOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Header = ({ user, onLogout, isLoading }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem('currency') || 'EUR');
  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    localStorage.setItem('currency', currency);
    window.dispatchEvent(new CustomEvent('currency-change', { detail: { currency } }));
  }, [currency]);

  useEffect(() => {
    let isActive = true;
    const loadRates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/rates?symbol=GBP`);
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Failed to fetch rates');
        if (isActive) {
          localStorage.setItem('rate_gbp', String(data.rate));
          localStorage.setItem('rate_gbp_date', data.date);
          window.dispatchEvent(new CustomEvent('currency-rate-update', { detail: { rate: data.rate, date: data.date } }));
        }
      } catch (err) {
        // Silently ignore rate fetch errors for UI.
      }
    };
    loadRates();
    const interval = setInterval(loadRates, 6 * 60 * 60 * 1000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, []);

  const userMenuItems = [
    {
      key: 'dashboard',
      label: <Link to="/dashboard" onClick={closeMobileMenu}><CloudOutlined /> Dashboard</Link>,
    },
    ...(user?.role === 'admin' ? [{
      key: 'admin',
      label: <Link to="/admin" onClick={closeMobileMenu}><SafetyOutlined /> Admin Panel</Link>,
    }] : []),
    { type: 'divider' },
    {
      key: 'logout',
      label: (
        <span onClick={() => { closeMobileMenu(); onLogout(); }}>
          <LogoutOutlined /> Logout
        </span>
      ),
    },
  ];

  return (
    <header className="main-header">
      <div className="header-container">
        <Link to="/" className="header-brand">
          <i className="fas fa-microchip"></i>
          <span>Elorimi</span>
        </Link>

        <nav className={`header-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <Link to="/" className="nav-link" onClick={closeMobileMenu}>Home</Link>
          <Link to="/#features" className="nav-link" onClick={closeMobileMenu}>Features</Link>
          <Link to="/#pricing" className="nav-link" onClick={closeMobileMenu}>Pricing</Link>
          <Link to="/docs" className="nav-link" onClick={closeMobileMenu}>Docs</Link>

          <div className="header-nav-mobile-actions">
            {isLoading ? null : user ? (
              <>
                <Link to="/dashboard" className="nav-mobile-action" onClick={closeMobileMenu}>
                  <CloudOutlined /> Dashboard
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className="nav-mobile-action" onClick={closeMobileMenu}>
                    <SafetyOutlined /> Admin Panel
                  </Link>
                )}
                <button className="nav-mobile-action nav-mobile-logout" onClick={() => { closeMobileMenu(); onLogout(); }}>
                  <LogoutOutlined /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-mobile-action" onClick={closeMobileMenu}>Login</Link>
                <Link to="/register" className="nav-mobile-action nav-mobile-primary" onClick={closeMobileMenu}>Get Started</Link>
              </>
            )}
          </div>
        </nav>

        <div className="header-actions">
          <div className="header-currency">
            <div className="currency-switcher">
              {['EUR', 'GBP'].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setCurrency(code)}
                  className={`currency-toggle ${currency === code ? 'active' : ''}`}
                  aria-pressed={currency === code}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="header-actions-loading" />
          ) : user ? (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']} overlayClassName="header-user-dropdown">
              <div className="header-user">
                <div className="user-avatar-small">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    <UserOutlined />
                  )}
                </div>
                <Text className="user-name-small">{user.name || user.email}</Text>
              </div>
            </Dropdown>
          ) : (
            <Space>
              <Link to="/login">
                <Button type="text" className="header-btn-text">Login</Button>
              </Link>
              <Link to="/register">
                <Button type="primary" className="header-btn-primary">Get Started</Button>
              </Link>
            </Space>
          )}

          <button
            className="mobile-menu-btn"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && <button className="header-menu-backdrop" aria-label="Close menu" onClick={closeMobileMenu} />}
    </header>
  );
};

export default Header;

